/**
 * File Usages Dialog
 * Shows where a file is being used
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material'
import {
  Close,
  Map,
  Groups,
  Person,
  Article,
} from '@mui/icons-material'
import { useFileUsages } from '../hooks/useFiles'

interface FileUsagesDialogProps {
  open: boolean
  onClose: () => void
  fileId: string | null
  fileName?: string
}

function getEntityIcon(entityType: string) {
  switch (entityType.toLowerCase()) {
    case 'adventure':
      return <Map color="primary" />
    case 'group':
      return <Groups color="secondary" />
    case 'profile':
      return <Person color="info" />
    default:
      return <Article color="action" />
  }
}

export function FileUsagesDialog({
  open,
  onClose,
  fileId,
  fileName,
}: FileUsagesDialogProps) {
  const { data: usages, isLoading, error } = useFileUsages(fileId || '')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        File Usages
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {fileName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing where <strong>{fileName}</strong> is being used
          </Typography>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load file usages. Please try again.
          </Alert>
        )}

        {!isLoading && !error && usages && usages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              This file is not currently used anywhere.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && usages && usages.length > 0 && (
          <List disablePadding>
            {usages.map((usage) => (
              <ListItem key={usage.id} divider>
                <ListItemIcon>{getEntityIcon(usage.entityType)}</ListItemIcon>
                <ListItemText
                  primary={usage.entityName || usage.entityId}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.secondary">
                        {usage.entityType}
                      </Typography>
                      {' - '}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {new Date(usage.createdDate).toLocaleDateString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
